//* Imports react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

//* Imports API methods
import { correctionsAPI } from "./correctionsAPI";

//* Imports types
import {
  type Correction,
  type CompletedCorrection,
  type CorrectionStatusResponse,
  type CreateCorrectionInput, 
  type CorrectionDetails,
  type UpdateCorrectionInput, 
} from "./types/types";

/**
 * @param token - The authentication token.
 * @returns An object containing methods and states for managing corrections.
 *
 * This hook provides functionalities for fetching, creating, updating, and deleting corrections.
 * It also includes pagination and search capabilities.
 *
 * OBS: Os tipos CreateCorrectionInput e UpdateCorrectionInput devem incluir a propriedade "questionType"
 * para que o backend receba o parâmetro necessário.
 */
export const useCorrections = (token: string) => {
  const queryClient = useQueryClient();

  //? States for search and pagination
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  //? Defines the query key with the search and pagination parameters
  const queryKey = [
    "corrections",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  //? Query to fetch all Corrections based on the provided parameters
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      correctionsAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const corrections: Correction[] = data?.corrections || [];
  const fetchedPagination = data?.pagination || pagination;

  //? Mutation to create a Correction
  const createCorrectionMutation = useMutation({
    mutationFn: (data: CreateCorrectionInput) => {
      // Garantindo que questionType está presente no payload
      if (!data.questionType) {
        console.warn("questionType não foi fornecido na criação da correção");
      }
      return correctionsAPI.create(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
    },
  });

  //? Mutation to update a Correction
  const updateCorrectionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCorrectionInput }) => {
      // Garantindo que questionType está presente no payload
      if (!data.questionType) {
        console.warn("questionType não foi fornecido na atualização da correção");
      }
      return correctionsAPI.update(id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
    },
  });

  //? Mutation to delete a Correction
  const deleteCorrectionMutation = useMutation({
    mutationFn: (id: string) => correctionsAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
    },
  });

  //? Function to fetch a Correction by ID
  const getCorrectionById = useCallback(
    async (id: string): Promise<CorrectionDetails | null> => {
      try {
        return await correctionsAPI.getById(id, token);
      } catch (error) {
        console.error("Error fetching correction by id:", error);
        return null;
      }
    },
    [token]
  );

  //? Function to fetch the status of a Correction by ID
  const getCorrectionStatus = useCallback(
    async (id: string): Promise<CorrectionStatusResponse | null> => {
      try {
        return await correctionsAPI.getStatus(id, token);
      } catch (error) {
        console.error("Error fetching correction status:", error);
        return null;
      }
    },
    [token]
  );

  //! Function to get Completed Correction
  const getCompletedCorrection = useCallback(
    async (id: string): Promise<CompletedCorrection | null> => {
      try {
        return await correctionsAPI.getByIdFull(id, token);
      } catch (error) {
        console.error("Error fetching completed correction:", error);
        return null;
      }
    },
    [token]
  );

  //? Function to change the page
  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  return {
    corrections,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createCorrection: createCorrectionMutation.mutate,
    updateCorrection: updateCorrectionMutation.mutate,
    deleteCorrection: deleteCorrectionMutation.mutate,
    getCorrectionById,
    getCompletedCorrection,
    changePage,
    isCreating: createCorrectionMutation.isPending,
    isUpdating: updateCorrectionMutation.isPending,
    isDeleting: deleteCorrectionMutation.isPending,
    getCorrectionStatus,
  };
};

export { correctionsAPI };
